import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  inject,
} from '@angular/core';
import {
  AbstractControl,
  ControlContainer,
  FormControl,
  FormGroup,
  NgForm,
  Validators,
} from '@angular/forms';
import { CreditCard } from '@fantaz/shared/data-access/models';
import { NgFormsManager } from '@ngneat/forms-manager';
import {
  CardValidationMask,
  CardValidator,
  DateValidators,
  FieldValidators,
} from '@fantaz/shared/utils/forms';
import { SUPPORTED_CREDIT_CARDS_DARK } from '@fantaz/shared/config';
import {
  ColorScheme,
  ColorSchemeService,
} from '@fantaz/shared/services/color-scheme';

@Component({
  selector: 'fz-credit-card-form',
  templateUrl: './credit-card-form.component.html',
  styleUrls: ['./credit-card-form.component.scss'],
  viewProviders: [{ provide: ControlContainer, useExisting: NgForm }],
})
export class CreditCardFormComponent implements OnInit, OnDestroy {
  _initialData: Partial<CreditCard> | null | undefined;

  @Input()
  set initialData(data: Partial<CreditCard> | null | undefined) {
    this._initialData = data;

    if (data && this.cardForm) {
      this.cardForm.patchValue(data, { emitEvent: false });
    }
  }

  get initialData() {
    return this._initialData;
  }

  @Input()
  type: 'CC' | 'ACH' | string = 'CC';

  @Input()
  disableSaveCard: boolean;

  @Input()
  disableSecondaryAction: boolean;

  @Input()
  loading = false;

  @Input()
  primaryActionName = 'Save credit card';

  @Input()
  secondaryActionName = 'Change Payment Method';

  @Input()
  disabledCardName = false;

  @Input()
  withdraw = false;

  @Output()
  submitCard = new EventEmitter<CreditCard>();

  @Output()
  changePaymentMethod = new EventEmitter<void>();

  private formsManager = inject(NgFormsManager);
  cardForm: FormGroup;

  constructor(private readonly colorSchemeService: ColorSchemeService) {
    this.colorSchemeService.init();
  }

  getCardNumberPlaceholder() {
    return (
      CardValidationMask.getCardValidationPattern(this.cardForm.value.number)
        ?.numberPlaceholder || ''
    );
  }

  getCardNumberMask() {
    return CardValidationMask.getCardValidationPattern(
      this.cardForm.value.number,
    )?.numberMask;
  }

  // FIXME: This is a fix for the Ctrl+V issue in the card number field
  onPaste(event: ClipboardEvent) {
    const value = event?.clipboardData?.getData('text/plain');
    if (value) {
      const cardNumberControl = this.cardForm.get('number');
      cardNumberControl?.setValue(value);
      this.onCardNumberChange();
    }
  }

  onCardNumberChange() {
    const cvvControl = this.cardForm.get('cvv');
    if (cvvControl?.value) {
      const mask = CardValidationMask.getCardValidationPattern(
        this.cardForm.value.number,
      )?.numberMask;
      if (mask && mask.length !== cvvControl.value.length) {
        cvvControl.setValue('');
        cvvControl.markAsPristine();
      }
    }
  }

  getCvvPlaceholder() {
    return (
      CardValidationMask.getCardValidationPattern(this.cardForm.value.number)
        ?.cvvPlaceholder || ''
    );
  }

  getCvvMask() {
    return CardValidationMask.getCardValidationPattern(
      this.cardForm.value.number,
    )?.cvvMask;
  }

  get getCardName() {
    const network = CardValidationMask.getCardValidationPattern(
      this.cardForm.value.number,
    )?.name;
    return network &&
      this.checkDarkCompatibility(network) &&
      this.isDarkScheme()
      ? `${network}-dark`
      : network;
  }

  private checkDarkCompatibility(network: string) {
    return SUPPORTED_CREDIT_CARDS_DARK.indexOf(network) >= 0;
  }

  isDarkScheme() {
    return this.colorSchemeService.get() === ColorScheme.Dark;
  }

  ngOnInit() {
    this.cardForm = new FormGroup({
      firstName: new FormControl<string>('', [
        FieldValidators.requiredValidator('firstName'),
        Validators.required,
        Validators.minLength(2),
      ]),
      lastName: new FormControl<string>('', [
        FieldValidators.requiredValidator('lastName'),
        Validators.required,
        Validators.minLength(2),
      ]),
      address: new FormControl(null, [Validators.required]),
      saveCreditCard: new FormControl<boolean>(true),
    });

    if (this.type === 'ACH') {
      this.cardForm.addControl(
        'routingNumber',
        new FormControl<string>('', [
          FieldValidators.requiredValidator('routingNumber'),
          Validators.required,
          CardValidator.routingNumber,
        ]),
      );
      this.cardForm.addControl(
        'accountNumber',
        new FormControl<string>('', [
          FieldValidators.requiredValidator('accountNumber'),
          Validators.required,
          CardValidator.accountNumber,
        ]),
      );
      this.cardForm.addControl('phoneNumber', new FormControl<string>(''));
    } else if (this.type === 'CC') {
      this.cardForm.addControl(
        'number',
        new FormControl<string>('', [
          FieldValidators.requiredValidator('creditCard'),
          Validators.required,
          CardValidator.cardNumber,
        ]),
      );
      this.cardForm.addControl(
        'cvv',
        new FormControl<string>('', [
          FieldValidators.requiredValidator('CVV'),
          Validators.required,
          (control: AbstractControl) =>
            CardValidator.cvvNumber(control, this.cardForm.value.number),
        ]),
      );
      this.cardForm.addControl(
        'expiration',
        new FormControl<string>('', [
          FieldValidators.requiredValidator('expirationDate'),
          Validators.required,
          DateValidators.expirationDateValidator,
        ]),
      );
    }

    this.formsManager.upsert('creditcard', this.cardForm, {
      persistState: false,
    });

    if (this._initialData) {
      this.cardForm.patchValue(this._initialData);

      if (this._initialData.number) {
        this.onCardNumberChange();
      }
    }
  }

  ngOnDestroy() {
    this.formsManager.unsubscribe('creditcard');
  }

  onSubmit() {
    this.cardForm.markAllAsTouched();

    if (this.cardForm.valid) {
      const value = Object.assign({}, this.cardForm.value);
      this.formatExpiration(value);
      this.submitCard.emit({
        ...value,
        name: `${value.firstName} ${value.lastName}`,
      });
    }
  }

  private formatExpiration(value: any) {
    if (this.type === 'CC') {
      const first = this.cardForm.value.expiration.substr(0, 2);
      const last = this.cardForm.value.expiration.substr(2);
      const formatted = `${first}/${last}`.replace('/', '/20');
      value.expiration = formatted;
    }
  }

  onChangePaymentMethod() {
    this.changePaymentMethod.emit();
  }

  getAccountLabel(): string {
    if (this.type === 'CC') {
      return 'card';
    } else if (this.type === 'ACH') {
      return 'eCheck (ACH)';
    }
    return '';
  }
}

@Component({
  selector: `fz-credit-card-form-actions, [fz-credit-card-form-actions], [fzCreditCardFormActions]`,
  template: `<ng-content></ng-content>`,
})
export class CreditCardFormPrimaryActionsComponent {
  @Input() content: string;
}
@Component({
  selector: `fz-credit-card-form-primary-action, [fz-credit-card-form-primary-action], [fzCreditCardFormPrimaryAction]`,
  template: `<button mat-flat-button color="primary">{{ content }}</button>`,
})
export class CreditCardFormPrimaryActionComponent {
  @Input() content: string;
}
